import { useState, useEffect, useMemo, useRef } from "react";
import { useApp } from "../context/AppContext";
import {
  TrendingUp, TrendingDown, DollarSign, Users,
  BarChart2, Calendar, Layers, Clock, ArrowUpRight,
  ArrowDownRight, Minus, Upload, CheckCircle, AlertCircle,
  X, Loader
} from "lucide-react";

const AUD = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
const fmt  = (n) => (n == null || isNaN(n) ? "—" : AUD.format(n));
const fmtK = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return "$" + (n / 1_000_000).toFixed(2) + "M";
  if (Math.abs(n) >= 1_000)     return "$" + (n / 1_000).toFixed(1) + "K";
  return AUD.format(n);
};
const pct = (n) => (n == null || isNaN(n) ? "—" : (n * 100).toFixed(1) + "%");
const parseMoney = (v) => parseFloat((v || '0').toString().replace(/[$,]/g, '')) || 0;

const parsePeriodLabel = (label) => {
  if (!label) return { start: null, end: null };
  const parts = label.split(" to ");
  if (parts.length !== 2) return { start: null, end: null };
  return { start: new Date(parts[0].trim()), end: new Date(parts[1].trim()) };
};

const VIEWS = [
  { id: "cashflow",   label: "Cash Flow",  Icon: DollarSign},
  { id: "reconcile",  label: "AP RCTIs",   Icon: CheckCircle},
  { id: "period",     label: "4-Weekly",   Icon: Layers    },
  { id: "daily",      label: "Daily",      Icon: Clock     },
  { id: "weekly",     label: "Weekly",     Icon: Calendar  },
  { id: "monthly",    label: "Monthly",    Icon: BarChart2 },
  { id: "annual",     label: "Annual",     Icon: TrendingUp},
  { id: "historical", label: "Historical", Icon: BarChart2 },
];

// ── Cash-flow forecast: current-month volume × AP RCTI unit rates ──────────────
// Default ex-GST unit rates ($/unit) read straight off the latest AP RCTI
// (Payment Period 14 Jun–11 Jul 2026). Editable in the UI (session-only).
// Stream mapping (confirmed by Paul from the RCTI line items):
//   Parcels ← Ordinary/Express + Signature + eParcel Consignments + eParcel Additional,
//             ALL @ 310.94c — volume tracked by WAHR (parcel_events parcel+express).
//   Depots  ← Drop Rate @ 737.94c — volume from the depots table.
//   DoE     ← DET Service @ 567.47c — volume from the doe table. (Shuttle Rate is a
//             separate open question — see the note in the UI.)
// Live-volume streams (Parcels, Depot) are priced at these AP unit rates.
// DoE / EP / Night Sort are NOT priced here — they read the tax invoices Paul
// generates in the app (tax-invoices-sb), so they match those invoices exactly.
const DEFAULT_RATES = {
  parcels: 3.1094,  // Ord/Exp + Signature + both eParcel lines @ 310.94c
  depot:   7.3794,  // Drop Rate (depot bags)                   @ 737.94c
  night: 4288.7815, // Night Sort — per scheduled night (Sun–Thu): AP paid $85,775.63 for 20
};
const GST = 1.1;
const pad2 = (n) => String(n).padStart(2, "0");
const ymKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const firstOfMonthISO = (y, m) => `${y}-${pad2(m + 1)}-01`;
const lastOfMonthISO  = (y, m) => `${y}-${pad2(m + 1)}-${pad2(new Date(y, m + 1, 0).getDate())}`;
const minISO = (a, b) => (a < b ? a : b);
// Operating days = Mon–Sat (AP delivers Mon–Sat; Sundays excluded) in [startISO,endISO].
function operatingDays(startISO, endISO) {
  if (!startISO || !endISO || endISO < startISO) return 0;
  let c = 0;
  for (let t = Date.parse(startISO + "T00:00:00Z"); t <= Date.parse(endISO + "T00:00:00Z"); t += 86400000) {
    if (new Date(t).getUTCDay() !== 0) c++;
  }
  return c;
}
// Last ~8 months as {key,label,y,m} for the month selector (newest first).
function recentMonths(n = 8) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: ymKey(d), y: d.getFullYear(), m: d.getMonth(), label: d.toLocaleString("en-AU", { month: "long", year: "numeric" }) });
  }
  return out;
}

// 28-day pay periods (Saturday end) — same cadence as the 4-Weekly tab / RCTI /
// the tax-invoice store, anchored on a known period end.
const PERIOD_ANCHOR_ISO = "2026-05-16";
const isoAddDays = (iso, d) => new Date(Date.parse(iso + "T00:00:00Z") + d * 86400000).toISOString().slice(0, 10);
function canonicalPeriodEnd(iso) {
  const day = (s) => Math.round(Date.parse(s + "T00:00:00Z") / 86400000);
  const delta = day(iso) - day(PERIOD_ANCHOR_ISO);
  return new Date((day(PERIOD_ANCHOR_ISO) + Math.ceil(delta / 28) * 28) * 86400000).toISOString().slice(0, 10);
}
const fmtDayMon = (iso) => new Date(iso + "T00:00:00Z").toLocaleString("en-AU", { day: "numeric", month: "short", timeZone: "UTC" });
// Last n pay periods, newest (current) first.
function recentPeriods(n = 8) {
  const todayISO = new Date().toISOString().slice(0, 10);
  let end = canonicalPeriodEnd(todayISO);
  const out = [];
  for (let i = 0; i < n; i++) {
    const start = isoAddDays(end, -27);
    out.push({ key: end, start, end, label: `${fmtDayMon(start)} – ${fmtDayMon(end)} ${end.slice(0, 4)}` });
    end = isoAddDays(end, -28);
  }
  return out;
}

const C = {
  bg:      "#04080f",
  surface: "#0d1829",
  card:    "#111d2e",
  border:  "#1e3048",
  blue:    "#2d7ef7",
  green:   "#16a34a",
  red:     "#dc2626",
  amber:   "#d97706",
  purple:  "#7c3aed",
  teal:    "#0891b2",
  lime:    "#65a30d",
  text:    "#e2eaf6",
  muted:   "#5a7a9a",
  label:   "#8fafc8",
};

const UPLOAD_STATE = { IDLE:"idle", READING:"reading", PARSING:"parsing", SAVING:"saving", SUCCESS:"success", ERROR:"error" };

function KpiCard({ label, value, sub, trendPct, icon: Icon, accent, onClick, open }) {
  const up = trendPct > 0, down = trendPct < 0;
  const TIcon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const tc = up ? C.green : down ? C.red : C.muted;
  const clickable = typeof onClick === "function";
  return (
    <div onClick={onClick} title={clickable ? "Click for the pay-group breakdown" : undefined}
      style={{ background:`linear-gradient(145deg,${C.card} 0%,#0a1525 100%)`, border:`1px solid ${open ? accent : accent+"44"}`, borderRadius:14, padding:"22px 24px", display:"flex", flexDirection:"column", gap:10, position:"relative", overflow:"hidden", cursor:clickable?"pointer":"default", transition:"border-color 0.15s" }}>
      <div style={{ position:"absolute", top:-24, right:-24, width:80, height:80, borderRadius:"50%", background:`${accent}12` }}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <span style={{ fontSize:11, fontWeight:700, color:C.label, textTransform:"uppercase", letterSpacing:"0.1em" }}>{label}</span>
        {clickable
          ? <span style={{ fontSize:11, fontWeight:700, color:accent, display:"flex", alignItems:"center", gap:4 }}>{open ? "Hide ▾" : "Breakdown ▸"}</span>
          : (Icon && <Icon size={15} color={accent}/>)}
      </div>
      <div style={{ fontSize:26, fontWeight:800, color:C.text, letterSpacing:"-0.02em", lineHeight:1 }}>{value}</div>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        {trendPct !== undefined && (
          <span style={{ display:"flex", alignItems:"center", gap:3, fontSize:12, fontWeight:700, color:tc }}>
            <TIcon size={12}/>{trendPct !== 0 ? Math.abs(trendPct).toFixed(1)+"%" : "—"}
          </span>
        )}
        {sub && <span style={{ fontSize:12, color:C.muted }}>{sub}</span>}
      </div>
    </div>
  );
}

// Reusable click-to-expand breakdown: one bar + amount + % per pay group.
// rows: [{ label, amount, color, note }]. Amounts already GST-inclusive.
function BreakdownPanel({ title, rows, totalLabel = "TOTAL (incl. GST)" }) {
  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const max = Math.max(1, ...rows.map((r) => r.amount || 0));
  return (
    <Card style={{ marginBottom:20, borderColor:`${C.blue}55` }}>
      <SectionTitle>{title}</SectionTitle>
      {rows.map((r) => (
        <div key={r.label} style={{ padding:"11px 0", borderBottom:`1px solid ${C.border}22` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:7, gap:12 }}>
            <span style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, color:C.text, fontWeight:600 }}>
              <span style={{ width:10, height:10, borderRadius:2, background:r.color, flexShrink:0 }}/>{r.label}
              {r.note && <span style={{ fontSize:11, color:C.muted, fontWeight:400 }}>{r.note}</span>}
            </span>
            <span style={{ fontSize:14, fontWeight:700, color:C.text, whiteSpace:"nowrap" }}>
              {fmt(r.amount)} <span style={{ color:C.muted, fontWeight:500, fontSize:11 }}>{total > 0 ? ((r.amount / total) * 100).toFixed(1) + "%" : "—"}</span>
            </span>
          </div>
          <div style={{ height:7, background:C.surface, borderRadius:4, overflow:"hidden" }}>
            <div style={{ width:`${max > 0 ? (r.amount / max) * 100 : 0}%`, height:"100%", background:r.color, borderRadius:4, transition:"width 0.2s" }}/>
          </div>
        </div>
      ))}
      <div style={{ display:"flex", justifyContent:"space-between", paddingTop:14 }}>
        <span style={{ fontSize:13, fontWeight:700, color:C.label }}>{totalLabel}</span>
        <span style={{ fontSize:16, fontWeight:800, color:C.green }}>{fmt(total)}</span>
      </div>
    </Card>
  );
}

// AP revenue line items (ex-GST keys on a periodData row) → breakdown rows.
const REV_COMPONENTS = [
  ["Ordinary / Express Parcels", "ordinary",   "blue"],
  ["Signature Items",            "signature",  "teal"],
  ["eParcel Consignments",       "eParcel",    "purple"],
  ["eParcel Additional",         "eParcelAdd", "lime"],
  ["Drop Rate",                  "dropRate",   "amber"],
  ["DET Service",                "det",        "green"],
  ["Shuttle Rate",               "shuttle",    "red"],
];
// Sum each revenue line item across the given periods, grossed to incl. GST.
function revenueRows(periods) {
  return REV_COMPONENTS.map(([label, key, c]) => ({
    label, color: C[c], amount: periods.reduce((s, p) => s + (p[key] || 0), 0) * 1.1,
  }));
}
// Sum the cost categories across the given periods (GST-inclusive basis).
function costRows(periods) {
  return [
    { label: "Contractor Payments (net of lease)", color: C.purple, amount: periods.reduce((s, p) => s + (p.contractorCost || 0), 0) },
    { label: "Employee Payroll",                   color: C.amber,  amount: periods.reduce((s, p) => s + (p.payrollCost || 0), 0) },
    { label: "Other Expenses",                     color: C.muted,  amount: periods.reduce((s, p) => s + (p.expenseCost || 0), 0) },
  ];
}

function BarRow({ label, revenue, cost, max, sub }) {
  const rPct = max > 0 ? (revenue/max)*100 : 0;
  const cPct = max > 0 ? (cost/max)*100 : 0;
  const m = revenue > 0 ? (revenue-cost)/revenue : null;
  const mc = m===null?C.muted:m>=0.15?C.green:m>=0.05?C.amber:C.red;
  return (
    <div style={{ padding:"14px 0", borderBottom:`1px solid ${C.border}` }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
        <div><span style={{ fontSize:13, fontWeight:600, color:C.text }}>{label}</span>{sub&&<span style={{ fontSize:11, color:C.muted, marginLeft:8 }}>{sub}</span>}</div>
        <span style={{ fontSize:12, fontWeight:700, color:mc }}>{m!==null?pct(m)+" margin":"No data"}</span>
      </div>
      <div style={{ position:"relative", height:6, background:C.surface, borderRadius:3, marginBottom:6, overflow:"hidden" }}>
        <div style={{ position:"absolute", left:0, top:0, height:"100%", width:`${rPct}%`, background:C.blue, borderRadius:3 }}/>
        <div style={{ position:"absolute", left:0, top:0, height:"100%", width:`${cPct}%`, background:`${C.red}99`, borderRadius:3 }}/>
      </div>
      <div style={{ display:"flex", justifyContent:"space-between" }}>
        <span style={{ fontSize:11, color:C.muted }}>Revenue {fmtK(revenue)}</span>
        <span style={{ fontSize:11, color:C.muted }}>Cost {fmtK(cost)}</span>
      </div>
    </div>
  );
}

function HistoChart({ data, height=140 }) {
  if (!data||data.length===0) return <div style={{ textAlign:"center", padding:40, color:C.muted, fontSize:13 }}>No data yet</div>;
  const maxVal = Math.max(...data.map(d=>Math.max(d.revenue||0,d.cost||0)));
  const bw = Math.max(20,Math.min(56,Math.floor(700/data.length)-6));
  return (
    <div style={{ overflowX:"auto", paddingBottom:4 }}>
      <div style={{ display:"flex", alignItems:"flex-end", gap:6, minWidth:data.length*(bw+6), paddingBottom:4 }}>
        {data.map((d,i)=>{
          const rh=maxVal>0?(d.revenue/maxVal)*height:0, ch=maxVal>0?(d.cost/maxVal)*height:0;
          const m=d.revenue>0?(d.revenue-d.cost)/d.revenue:null;
          const mc=m===null?C.muted:m>=0.1?C.green:m>=0?C.amber:C.red;
          return (
            <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
              <span style={{ fontSize:9, fontWeight:700, color:mc }}>{m!==null?pct(m):"—"}</span>
              <div style={{ display:"flex", alignItems:"flex-end", gap:2, height }}>
                <div style={{ width:bw/2-2, height:rh, background:`${C.blue}cc`, borderRadius:"3px 3px 0 0" }}/>
                <div style={{ width:bw/2-2, height:ch, background:`${C.red}99`, borderRadius:"3px 3px 0 0" }}/>
              </div>
              <span style={{ fontSize:9, color:C.muted, textAlign:"center", width:bw, wordBreak:"break-word", lineHeight:1.2 }}>{d.label}</span>
            </div>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:16, marginTop:12 }}>
        {[["Revenue",C.blue],["Cost",C.red]].map(([l,c])=>(
          <div key={l} style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:10, height:10, borderRadius:2, background:c }}/>
            <span style={{ fontSize:11, color:C.muted }}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PvaTable({ pvas }) {
  if (!pvas||pvas.length===0) return <p style={{ color:C.muted, fontSize:13 }}>No PVA data for this period.</p>;
  return (
    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
      <thead><tr style={{ borderBottom:`1px solid ${C.border}` }}>
        {["Type","Ref","Claimed","Approved","Variance"].map(h=>(
          <th key={h} style={{ textAlign:"left", padding:"8px 0", color:C.label, fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:"0.06em" }}>{h}</th>
        ))}
      </tr></thead>
      <tbody>{pvas.map((p,i)=>{
        const claimed=parseMoney(p.Amount_Claimed), approved=parseMoney(p.Amount_Approved);
        const variance=parseMoney(p.Variance)||(approved-claimed);
        return (
          <tr key={i} style={{ borderBottom:`1px solid ${C.border}22` }}>
            <td style={{ padding:"10px 0", color:C.text, fontWeight:600 }}>{p.PVA_Type}</td>
            <td style={{ padding:"10px 0", color:C.muted }}>{p.PVA_Ref||"—"}</td>
            <td style={{ padding:"10px 0", color:C.text }}>{fmt(claimed)}</td>
            <td style={{ padding:"10px 0", color:C.green, fontWeight:700 }}>{fmt(approved)}</td>
            <td style={{ padding:"10px 0", color:variance<0?C.red:variance>0?C.green:C.muted, fontWeight:600 }}>
              {variance!==0?(variance>0?"+":"")+fmt(variance):"—"}
            </td>
          </tr>
        );
      })}</tbody>
    </table>
  );
}

function ContractorTable({ rows }) {
  const [expanded, setExpanded] = useState(false);
  if (!rows || rows.length === 0) return (
    <p style={{ color:C.muted, fontSize:13 }}>No RCTI history data for this period. Run an RCTI send to populate.</p>
  );
  const display = expanded ? rows : rows.slice(0, 10);
  const totalGross = rows.reduce((s, r) => s + parseMoney(r.Gross_Total), 0);
  const totalLease = rows.filter(r => parseMoney(r.Gross_Total) > 0).reduce((s, r) => s + parseMoney(r.Lease_Fee), 0);
  const totalNet = rows.reduce((s, r) => s + parseMoney(r.Net_Payable), 0);
  return (
    <div>
      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
        <thead>
          <tr style={{ borderBottom:`1px solid ${C.border}` }}>
            {["Invoice","Driver","Company","Parcels","Gross Total","Lease Fee","Net Payable"].map(h=>(
              <th key={h} style={{ textAlign:"left", padding:"8px 6px", color:C.label, fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {display.map((r,i)=>{
            const gross = parseMoney(r.Gross_Total);
            const lease = gross > 0 ? parseMoney(r.Lease_Fee) : 0;
            const net   = parseMoney(r.Net_Payable);
            return (
              <tr key={i} style={{ borderBottom:`1px solid ${C.border}11`, background: i%2===0?'transparent':`${C.surface}66` }}>
                <td style={{ padding:"8px 6px", color:C.muted, fontSize:11 }}>{r.Invoice_No}</td>
                <td style={{ padding:"8px 6px", color:C.text, fontWeight:600 }}>{r.Login}</td>
                <td style={{ padding:"8px 6px", color:C.muted }}>{r.Company}</td>
                <td style={{ padding:"8px 6px", color:C.text }}>{parseInt(r.Parcels||0).toLocaleString()}</td>
                <td style={{ padding:"8px 6px", color:C.text, fontWeight:600 }}>{fmt(gross)}</td>
                <td style={{ padding:"8px 6px", color:lease>0?C.teal:C.muted, fontWeight:lease>0?600:400 }}>{lease>0?fmt(lease):"—"}</td>
                <td style={{ padding:"8px 6px", color:C.green, fontWeight:700 }}>{fmt(net)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop:`2px solid ${C.border}` }}>
            <td colSpan={4} style={{ padding:"10px 6px", color:C.label, fontSize:12, fontWeight:700 }}>TOTAL — {rows.length} contractors</td>
            <td style={{ padding:"10px 6px", color:C.blue, fontWeight:800, fontSize:13 }}>{fmt(totalGross)}</td>
            <td style={{ padding:"10px 6px", color:C.teal, fontWeight:800, fontSize:13 }}>{fmt(totalLease)}</td>
            <td style={{ padding:"10px 6px", color:C.green, fontWeight:800, fontSize:13 }}>{fmt(totalNet)}</td>
          </tr>
        </tfoot>
      </table>
      {rows.length > 10 && (
        <button onClick={()=>setExpanded(v=>!v)} style={{ marginTop:12, background:"none", border:`1px solid ${C.border}`, borderRadius:8, color:C.muted, fontSize:12, padding:"6px 14px", cursor:"pointer", width:"100%" }}>
          {expanded ? "Show less ▲" : `Show all ${rows.length} contractors ▼`}
        </button>
      )}
    </div>
  );
}

function Card({ children, style={} }) {
  return <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:24, ...style }}>{children}</div>;
}
function SectionTitle({ children }) {
  return <h3 style={{ fontSize:13, fontWeight:700, color:C.label, textTransform:"uppercase", letterSpacing:"0.08em", margin:"0 0 16px" }}>{children}</h3>;
}
function EmptyBanner({ msg }) {
  return (
    <div style={{ background:C.card, border:`1px dashed ${C.border}`, borderRadius:14, padding:"60px 20px", textAlign:"center" }}>
      <DollarSign size={40} color={C.border} style={{ marginBottom:12 }}/>
      <p style={{ color:C.muted, fontSize:14, margin:0 }}>{msg}</p>
    </div>
  );
}

function UploadPanel({ onSuccess }) {
  const [state, setState] = useState(UPLOAD_STATE.IDLE);
  const [statusMsg, setStatus] = useState("");
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();
  const busy = [UPLOAD_STATE.READING, UPLOAD_STATE.PARSING, UPLOAD_STATE.SAVING].includes(state);
  const processFile = async (file) => {
    if (!file || file.type !== "application/pdf") { setState(UPLOAD_STATE.ERROR); setStatus("Please select a PDF file."); return; }
    setPreview({ name: file.name, size: (file.size/1024).toFixed(0)+" KB" });
    setState(UPLOAD_STATE.READING); setStatus("Reading PDF…");
    const base64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.onerror = () => rej(new Error("Failed to read file")); r.readAsDataURL(file); });
    setState(UPLOAD_STATE.PARSING); setStatus("Claude is reading the invoice…");
    const parseRes = await fetch("/.netlify/functions/parse-ap-rcti", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ pdf_base64: base64, filename: file.name }) });
    const parseJson = await parseRes.json();
    if (!parseRes.ok || !parseJson.success) { setState(UPLOAD_STATE.ERROR); setStatus(parseJson.error||"Parse failed"); return; }
    setState(UPLOAD_STATE.SAVING); setStatus("Saving to Google Sheets…");
    const saveRes = await fetch("/.netlify/functions/appsscript", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ action:"saveAPRevenue", data: parseJson.data }) });
    const saveJson = await saveRes.json();
    if (!saveRes.ok || !saveJson.success) { setState(UPLOAD_STATE.ERROR); setStatus(saveJson.error||"Save failed"); return; }
    setState(UPLOAD_STATE.SUCCESS); setStatus(`Saved: ${parseJson.data.period_label}`);
    if (onSuccess) onSuccess(parseJson.data);
  };
  const handleFile = async (file) => { try { await processFile(file); } catch (err) { setState(UPLOAD_STATE.ERROR); setStatus(err.message); } };
  return (
    <Card>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <SectionTitle>Import AP RCTI</SectionTitle>
        {state===UPLOAD_STATE.SUCCESS && <button onClick={()=>{setState(UPLOAD_STATE.IDLE);setPreview(null);setStatus("");}} style={{ background:"none", border:"none", color:C.muted, cursor:"pointer", display:"flex", alignItems:"center", gap:4, fontSize:12 }}><X size={12}/> Upload another</button>}
      </div>
      <div onDragOver={e=>{e.preventDefault();setDragOver(true);}} onDragLeave={()=>setDragOver(false)} onDrop={e=>{e.preventDefault();setDragOver(false);const f=e.dataTransfer.files[0];if(f)handleFile(f);}} onClick={()=>!busy&&fileRef.current?.click()} style={{ border:`2px dashed ${dragOver?C.blue:C.border}`, borderRadius:10, padding:"32px 20px", textAlign:"center", cursor:busy?"default":"pointer", background:dragOver?`${C.blue}08`:C.surface, transition:"all 0.15s", marginBottom:16 }}>
        <input ref={fileRef} type="file" accept="application/pdf" style={{ display:"none" }} onChange={e=>{const f=e.target.files[0];if(f)handleFile(f);e.target.value="";}}/>
        {state===UPLOAD_STATE.SUCCESS ? (<><CheckCircle size={36} color={C.green} style={{ marginBottom:10 }}/><p style={{ color:C.green, fontWeight:700, fontSize:15, margin:"0 0 4px" }}>Import complete</p><p style={{ color:C.muted, fontSize:12, margin:0 }}>{preview?.name}</p></>)
        : busy ? (<><Loader size={36} color={C.blue} style={{ marginBottom:10, animation:"spin 1s linear infinite" }}/><p style={{ color:C.blue, fontWeight:600, fontSize:14, margin:"0 0 4px" }}>{statusMsg}</p><p style={{ color:C.muted, fontSize:12, margin:0 }}>{preview?.name} · {preview?.size}</p></>)
        : (<><Upload size={36} color={C.muted} style={{ marginBottom:10 }}/><p style={{ color:C.text, fontWeight:600, fontSize:15, margin:"0 0 4px" }}>Drop AP RCTI PDF here</p><p style={{ color:C.muted, fontSize:12, margin:"0 0 14px" }}>or click to select · PDF only</p><div style={{ display:"inline-block", padding:"7px 20px", background:`${C.blue}22`, border:`1px solid ${C.blue}44`, borderRadius:8, color:C.blue, fontSize:13, fontWeight:600 }}>Select PDF</div></>)}
      </div>
      {state===UPLOAD_STATE.ERROR && statusMsg && <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", borderRadius:8, background:`${C.red}15`, border:`1px solid ${C.red}33` }}><AlertCircle size={14} color={C.red}/><span style={{ fontSize:13, color:C.red }}>{statusMsg}</span></div>}
      {state===UPLOAD_STATE.IDLE && <p style={{ fontSize:12, color:C.muted, margin:"12px 0 0", lineHeight:1.6 }}><span style={{ fontWeight:600, color:C.label }}>How it works: </span>Upload the PDF from AP → Claude reads every line item → saves to AP_Revenue &amp; AP_PVA tabs → dashboard updates.</p>}
    </Card>
  );
}

function ExtractedPreview({ data }) {
  if (!data) return null;
  return (
    <Card style={{ borderColor:`${C.green}33` }}>
      <SectionTitle>Extracted from PDF</SectionTitle>
      <div className="stack-mobile" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4, fontSize:13, marginBottom:16 }}>
        {[["Period",data.period_label],["SAP Invoice",data.sap_invoice_no],["Total inc GST",fmt(data.total_inc_gst)],["GST",fmt(data.gst)],["Total ex GST",fmt(data.total_ex_gst)],["PVA Total",fmt(data.pva_total)]].map(([k,v])=>(
          <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${C.border}22` }}><span style={{ color:C.muted }}>{k}</span><span style={{ color:C.text, fontWeight:600 }}>{v||"—"}</span></div>
        ))}
      </div>
      {data.pvas?.length>0 && (<><div style={{ fontSize:11, color:C.label, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>PVAs</div>{data.pvas.map((p,i)=>(<div key={i} style={{ display:"flex", justifyContent:"space-between", fontSize:13, padding:"5px 0", borderBottom:`1px solid ${C.border}11` }}><span style={{ color:C.muted }}>{p.pva_type}{p.pva_ref?` (#${p.pva_ref})`:""}</span><span style={{ color:C.green, fontWeight:600 }}>{fmt(p.amount_approved)}</span></div>))}</>)}
    </Card>
  );
}

function RateInput({ value, onChange }) {
  return (
    <input
      type="number" step="0.0001" min="0" value={value}
      onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
      style={{ width:90, background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:"5px 8px", fontSize:12, fontWeight:600, textAlign:"right" }}
    />
  );
}

// Current-month volume → expected cash flow, priced at AP RCTI unit rates.
// Two kinds of stream:
//   • Contract-rate (Parcels, Depot, Night Sort) — a countable quantity × an AP unit
//     rate. AP pays these on the RCTI without us raising anything, so whatever has
//     already happened is BANKED and only the remainder of the window is projected.
//   • Invoice      (DoE, EP, Additional Time)   — the ex-GST total of the tax invoice
//     Paul generates in the app for that period (tax-invoices-sb), so the figure
//     MATCHES the invoice issued. Falls back to the most recent invoice as an
//     estimate, and accepts a manual override, until this window's is generated.
const INV_STREAMS = [
  { key: "doe",   label: "DoE",           note: "Your generated DoE tax invoice" },
  { key: "wave",  label: "EP (2nd Wave)", note: "Your generated 2nd Wave EP invoice" },
  // Additional time is exceptional by nature — there is no model to project it, so it
  // shows only once an invoice for the window has actually been generated.
  { key: "extra", label: "Additional Time", note: "Time worked outside the contracted sort window" },
];

// ── Interim projection models for the self-generated invoice streams ──────────
// Used until the actual invoice for the window is generated (which supersedes them).
// Rates mirror the Tax Invoices page so projections match how these are billed.
const EP_WAVE_NET_RATE = 5.91 - 2.48;   // 2nd Wave EP net: unit price − per-item adjustment (ex GST)
// Night Sort (from 1 Aug 2026): a contract LINE ITEM, not a PVA we invoice — AP pays a
// flat rate per scheduled night, Sunday and Mon–Thu alike, so nights already run are
// banked revenue (Paul, 2026-09-09). The rate is what AP actually paid: $85,775.63 for a
// 20-night period = $4,288.7815 a night, kept to 4 dp so 20 nights ties to the cent
// (Paul, 2026-09-10). The old build-up — 1 leader + 9 crew × Sun 6.5h / Mon–Thu 6.0h
// @ $58.30/$52.73 — priced the night at $3,250.51.
const NIGHT_PER_NIGHT = DEFAULT_RATES.night;
// DoE standard invoice (ex GST): 20 service days @ 262.9273 + 12 schools × 8 collections @ 5.3208
// (fee increase, Oct 2026 — the Tax Invoices Rates tab holds the saved figures).
const DOE_PERIOD_EX = 20 * 262.9273 + 12 * 8 * 5.3208;
const DOE_OP_DAYS_PER_PERIOD = 24;      // Mon–Sat in a 28-day period (accrual basis)

// Count Sunday and Mon–Thu nights in [startISO,endISO]; how many are on/before cutISO.
function countNightShifts(startISO, endISO, cutISO) {
  let sun = 0, wk = 0, sunB = 0, wkB = 0;
  for (let t = Date.parse(startISO + "T00:00:00Z"); t <= Date.parse(endISO + "T00:00:00Z"); t += 86400000) {
    const dt = new Date(t); const d = dt.getUTCDay(); const iso = dt.toISOString().slice(0, 10);
    if (d === 0) { sun++; if (iso <= cutISO) sunB++; }
    else if (d >= 1 && d <= 4) { wk++; if (iso <= cutISO) wkB++; }
  }
  return { sun, wk, sunB, wkB };
}
function CashFlowView() {
  const months  = useMemo(() => recentMonths(8), []);
  const periods = useMemo(() => recentPeriods(8), []);
  const todayISO = new Date().toISOString().slice(0, 10);
  const [mode, setMode]       = useState("period");   // 'period' (28-day) | 'month' (calendar)
  const [monthKey, setMonthKey]   = useState(months[0].key);
  const [periodKey, setPeriodKey] = useState(periods[0].key);
  const [rates,  setRates]    = useState(DEFAULT_RATES);
  const [invOverride, setInvOverride] = useState({ doe: "", wave: "", extra: "" });
  const [cur,  setCur]  = useState(null);   // selected-window forecast payload
  const [prev, setPrev] = useState(null);   // prior-window payload (context)
  const [invoices, setInvoices] = useState([]);  // stored generated tax invoices
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [openCard, setOpenCard] = useState(null);   // 'projected' | 'banked' | null

  // Resolve the active window (calendar month OR 28-day pay period) + its prior window.
  const win = useMemo(() => {
    if (mode === "period") {
      const p = periods.find((x) => x.key === periodKey) || periods[0];
      const prevEnd = isoAddDays(p.start, -1);
      return { start: p.start, end: p.end, label: p.label, prevStart: isoAddDays(prevEnd, -27), prevEnd, isPeriod: true, unit: "Period" };
    }
    const m = months.find((x) => x.key === monthKey) || months[0];
    const pd = new Date(m.y, m.m - 1, 1);
    return { start: firstOfMonthISO(m.y, m.m), end: lastOfMonthISO(m.y, m.m), label: m.label,
      prevStart: firstOfMonthISO(pd.getFullYear(), pd.getMonth()), prevEnd: lastOfMonthISO(pd.getFullYear(), pd.getMonth()), isPeriod: false, unit: "Month" };
  }, [mode, monthKey, periodKey, months, periods]);

  const { start, end, prevStart, prevEnd, unit } = win;
  const elapsedEnd = minISO(end, todayISO);          // to-date cut-off
  const windowComplete = end <= todayISO;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      setInvOverride({ doe: "", wave: "", extra: "" });   // reseed defaults per window
      try {
        const call = (s, e) => fetch("/.netlify/functions/revenue-forecast-sb", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "getMonthForecast", start: s, end: e }),
        }).then((r) => r.json());
        const [c, p, ti] = await Promise.all([
          call(start, elapsedEnd),
          call(prevStart, prevEnd),
          fetch("/.netlify/functions/tax-invoices-sb", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "getTaxInvoices" }),
          }).then((r) => r.json()).catch(() => ({ success: false })),
        ]);
        if (!alive) return;
        if (!c.success) throw new Error(c.error || "Forecast fetch failed");
        setCur(c); setPrev(p.success ? p : null);
        setInvoices(ti.success ? (ti.invoices || []) : []);
      } catch (e) { if (alive) setErr(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [mode, monthKey, periodKey]);

  const opDaysWin     = operatingDays(start, end);
  const opDaysElapsed = operatingDays(start, elapsedEnd);
  const factor = opDaysElapsed > 0 ? opDaysWin / opDaysElapsed : null;   // to-date → full window
  const v = cur?.volumes || { parcels: 0, express: 0, doe: 0, depot: 0 };
  const pv = prev?.volumes || null;

  const rateN = (x) => (x === "" || x == null || isNaN(x) ? 0 : x);
  // Scheduled Sun–Thu nights: how many fall in the window, and how many have already run.
  const nc     = countNightShifts(start, end, elapsedEnd);
  const prevNc = countNightShifts(prevStart, prevEnd, prevEnd);
  // Contract-rate streams. Parcels = all 310.94c delivered units (parcel + express incl.
  // eParcel). Night Sort's quantity is the roster, which is known for the whole window —
  // so it carries an exact `projVol` rather than being extrapolated from operating days.
  const streams = [
    { key: "parcels", label: "Parcels",      source: "WAHR — all delivered @ 310.94c (incl. eParcel)", vol: v.parcels + v.express, rate: rateN(rates.parcels), prevVol: pv ? pv.parcels + pv.express : null },
    { key: "depot",   label: "Depot / Drop", source: "Depot bags × Drop Rate", vol: v.depot, rate: rateN(rates.depot), prevVol: pv?.depot },
    { key: "night",   label: "Night Sort",   source: "Scheduled nights (Sun–Thu) × flat contract rate", vol: nc.sunB + nc.wkB, rate: rateN(rates.night), prevVol: prevNc.sun + prevNc.wk, projVol: nc.sun + nc.wk },
  ].map((s) => {
    const mtdEx   = s.vol * s.rate;
    const projVol = s.projVol != null ? s.projVol : (factor ? Math.round(s.vol * factor) : s.vol);
    const projEx  = projVol * s.rate;
    return { ...s, mtdEx, projVol, projEx };
  });

  // Invoice streams — read the generated tax invoices for the window. In period mode
  // that's an EXACT period-end match; in month mode, any invoice whose period ends in it.
  const invByType = (type) => invoices.filter((iv) => iv.type === type);
  const invIn = (type, s, e) => (win.isPeriod
    ? invByType(type).filter((iv) => iv.periodEnd === e)
    : invByType(type).filter((iv) => iv.periodEnd >= s && iv.periodEnd <= e));

  // Interim projection models — the default until the real invoice is generated + stored.
  const projExpress = factor ? Math.round(v.express * factor) : v.express;
  const MODEL = {
    wave:  { proj: projExpress * EP_WAVE_NET_RATE,                       note: `model · ${projExpress.toLocaleString("en-AU")} EP × $${EP_WAVE_NET_RATE.toFixed(2)}` },
    doe:   { proj: (DOE_PERIOD_EX / DOE_OP_DAYS_PER_PERIOD) * opDaysWin, note: "model · standard DoE invoice" },
    extra: { proj: 0,                                                    note: "none raised for this window" },
  };

  const invStreams = INV_STREAMS.map((x) => {
    const inWin = invIn(x.key, start, end);
    const storedEx = inWin.reduce((s, iv) => s + (Number(iv.subtotalEx) || 0), 0);
    const bankedEx = inWin.filter((iv) => iv.periodEnd <= todayISO).reduce((s, iv) => s + (Number(iv.subtotalEx) || 0), 0);
    const hasStored = inWin.length > 0;
    const lastInv = invByType(x.key)[0] || null;   // newest stored invoice overall (sorted desc)
    // Default projection precedence:
    //   1) an invoice generated for THIS window (exact, becomes Banked once issued)
    //   2) DoE only: your most recent generated DoE invoice carried forward
    //   3) the pricing model
    let defaultEx, prov;
    if (hasStored) {
      defaultEx = storedEx; prov = inWin.map((iv) => `#${iv.invoiceNum || "?"}`).join(", ");
    } else if (x.key === "doe" && lastInv) {
      defaultEx = Number(lastInv.subtotalEx) || 0; prov = `last invoice #${lastInv.invoiceNum || "?"} (${lastInv.periodEnd})`;
    } else {
      defaultEx = MODEL[x.key].proj; prov = MODEL[x.key].note;
    }
    const ov = invOverride[x.key];
    const ovN = ov === "" || ov == null || isNaN(ov) ? null : Number(ov);
    const projEx = ovN != null ? ovN : defaultEx;
    const prevEx = invIn(x.key, prevStart, prevEnd).reduce((s, iv) => s + (Number(iv.subtotalEx) || 0), 0);
    return { ...x, inWin, storedEx, bankedEx, defaultEx, projEx, prevEx, prov, estimated: !hasStored };
  });

  const volMtdEx  = streams.reduce((s, x) => s + x.mtdEx, 0);
  const volProjEx = streams.reduce((s, x) => s + x.projEx, 0);
  const invMtdEx  = invStreams.reduce((s, x) => s + x.bankedEx, 0);
  const invProjEx = invStreams.reduce((s, x) => s + x.projEx, 0);
  const totalMtdEx  = volMtdEx + invMtdEx;
  const totalProjEx = volProjEx + invProjEx;
  const totalProjIncl = totalProjEx * GST;
  const totalMtdIncl  = totalMtdEx * GST;

  // Pay-group breakdown (for the click-to-expand KPI panels). Incl. GST applied in render.
  const invMap = Object.fromEntries(invStreams.map((x) => [x.key, x]));
  const groups = [
    { label: "Parcels",     color: C.blue,   projEx: streams[0].projEx,      bankedEx: streams[0].mtdEx },
    { label: "Depots",      color: C.teal,   projEx: streams[1].projEx,      bankedEx: streams[1].mtdEx },
    { label: "Night Sort",  color: C.amber,  projEx: streams[2].projEx,      bankedEx: streams[2].mtdEx },
    { label: "2nd Wave EP", color: C.purple, projEx: invMap.wave?.projEx || 0,  bankedEx: invMap.wave?.bankedEx || 0,  note: invMap.wave?.estimated ? "est." : "" },
    { label: "DoE",         color: C.lime,   projEx: invMap.doe?.projEx || 0,   bankedEx: invMap.doe?.bankedEx || 0,   note: invMap.doe?.estimated ? "est." : "" },
  ];

  const hasAnyVol = (v.parcels + v.express + v.doe + v.depot) > 0;
  const selOpts = mode === "period" ? periods : months;
  const selKey  = mode === "period" ? periodKey : monthKey;
  const setSel  = mode === "period" ? setPeriodKey : setMonthKey;

  return (
    <div>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:700, margin:"0 0 4px" }}>Cash Flow Forecast — {win.label}</h2>
          <p style={{ fontSize:13, color:C.muted, margin:0 }}>
            Live volume × AP rates + your generated DoE / EP / Night Sort invoices · projected to {unit.toLowerCase()}-end on a {opDaysElapsed}/{opDaysWin} operating-day run-rate
            {cur?.lastDataDate ? ` · data to ${cur.lastDataDate}` : ""}
          </p>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ display:"flex", background:C.surface, borderRadius:10, padding:3, border:`1px solid ${C.border}` }}>
            {[["period","Pay period"],["month","Calendar month"]].map(([id,lbl]) => (
              <button key={id} onClick={() => setMode(id)} style={{ padding:"7px 14px", borderRadius:8, border:"none", background:mode===id?C.blue:"transparent", color:mode===id?"#fff":C.muted, fontWeight:700, fontSize:12, cursor:"pointer", transition:"all 0.15s" }}>{lbl}</button>
            ))}
          </div>
          <select value={selKey} onChange={(e) => setSel(e.target.value)} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, padding:"8px 14px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
            {selOpts.map((o) => <option key={o.key} value={o.key}>{o.label}{o.key === selOpts[0].key ? " (current)" : ""}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:C.muted, fontSize:13 }}>Loading live volumes…</div>
      ) : err ? (
        <div style={{ padding:"14px 18px", borderRadius:10, background:`${C.red}15`, border:`1px solid ${C.red}33`, color:C.red, fontSize:13 }}>
          Couldn’t load forecast: {err}
        </div>
      ) : (
        <>
          {/* Headline */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(220px, 100%),1fr))", gap:14, marginBottom:20 }}>
            <KpiCard label={`${windowComplete ? unit+" Total" : "Projected "+unit} (incl. GST)`} value={fmt(totalProjIncl)} icon={DollarSign} accent={C.green}
              sub={`Ex GST: ${fmt(totalProjEx)}`}
              onClick={() => setOpenCard((o) => o === "projected" ? null : "projected")} open={openCard === "projected"} />
            <KpiCard label="Banked So Far (incl. GST)" value={fmt(totalMtdIncl)} icon={TrendingUp} accent={C.blue}
              sub={`ex GST ${fmt(totalMtdEx)}`}
              onClick={() => setOpenCard((o) => o === "banked" ? null : "banked")} open={openCard === "banked"} />
            <KpiCard label="Parcel Volume to Date" value={(v.parcels + v.express).toLocaleString("en-AU")} icon={Layers} accent={C.purple}
              sub="all delivered @ 310.94c (incl. eParcel)" />
            <KpiCard label="Operating Days" value={`${opDaysElapsed} / ${opDaysWin}`} icon={Calendar} accent={C.amber}
              sub={windowComplete ? `${unit.toLowerCase()} complete` : `elapsed / in ${unit.toLowerCase()} (Mon–Sat)`} />
          </div>

          {openCard === "projected" && (
            <BreakdownPanel title={`Projected ${unit} income by pay group (incl. GST)`}
              rows={groups.map((g) => ({ label: g.label, color: g.color, amount: g.projEx * GST, note: g.note }))} />
          )}
          {openCard === "banked" && (
            <BreakdownPanel title="Banked so far by pay group (incl. GST)"
              rows={groups.map((g) => ({ label: g.label, color: g.color, amount: g.bankedEx * GST, note: g.note }))} />
          )}

          {!hasAnyVol && (
            <div style={{ marginBottom:16, padding:"12px 16px", borderRadius:10, background:`${C.amber}12`, border:`1px solid ${C.amber}33`, color:C.amber, fontSize:13 }}>
              No live volume recorded for {win.label} yet — showing the projection basis. Prior-{unit.toLowerCase()} actuals are shown for reference in the table below.
            </div>
          )}

          {/* Per-stream table */}
          <Card style={{ marginBottom:16 }}>
            <SectionTitle>Revenue Streams — {win.label}</SectionTitle>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:700 }}>
                <thead><tr style={{ borderBottom:`1px solid ${C.border}` }}>
                  {["Stream","Rate / Source","Volume","Banked",windowComplete?`${unit} Total`:`Projected ${unit}`,`Prior ${unit}`].map((h,i) => (
                    <th key={h} style={{ textAlign:i===0?"left":"right", padding:"8px 6px", color:C.label, fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {/* Live-volume streams (Parcels, Depot) */}
                  {streams.map((s) => (
                    <tr key={s.key} style={{ borderBottom:`1px solid ${C.border}22` }}>
                      <td style={{ padding:"11px 6px" }}>
                        <div style={{ color:C.text, fontWeight:600 }}>{s.label}</div>
                        <div style={{ color:C.muted, fontSize:11 }}>{s.source}</div>
                      </td>
                      <td style={{ padding:"11px 6px", textAlign:"right" }}>
                        <RateInput value={rates[s.key]} onChange={(x) => setRates((r) => ({ ...r, [s.key]: x }))} />
                      </td>
                      <td style={{ padding:"11px 6px", textAlign:"right", color:C.text }}>{s.key === "night" ? `${s.vol} of ${s.projVol} nights` : s.vol.toLocaleString("en-AU")}</td>
                      <td style={{ padding:"11px 6px", textAlign:"right", color:C.text, fontWeight:600 }}>{fmt(s.mtdEx)}</td>
                      <td style={{ padding:"11px 6px", textAlign:"right", color:C.green, fontWeight:700 }}>{fmt(s.projEx)}</td>
                      <td style={{ padding:"11px 6px", textAlign:"right", color:C.muted }}>{s.prevVol == null ? "—" : s.key === "night" ? `${s.prevVol} nights` : s.prevVol.toLocaleString("en-AU")}</td>
                    </tr>
                  ))}
                  {/* Section divider */}
                  <tr><td colSpan={6} style={{ padding:"8px 6px 4px", color:C.label, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em" }}>Self-generated tax invoices</td></tr>
                  {/* Invoice streams (DoE, EP 2nd Wave, Night Sort) */}
                  {invStreams.map((x) => (
                    <tr key={x.key} style={{ borderBottom:`1px solid ${C.border}22` }}>
                      <td style={{ padding:"11px 6px" }}>
                        <div style={{ color:C.text, fontWeight:600 }}>{x.label}</div>
                        <div style={{ color:C.muted, fontSize:11 }}>{x.note}</div>
                      </td>
                      <td style={{ padding:"11px 6px", textAlign:"right" }}>
                        <div style={{ fontSize:11, color: x.estimated ? C.amber : C.teal }}>{x.prov}</div>
                        <input type="number" step="0.01" min="0" value={invOverride[x.key]} placeholder={x.defaultEx ? x.defaultEx.toFixed(2) : "override"}
                          onChange={(e) => setInvOverride((m) => ({ ...m, [x.key]: e.target.value === "" ? "" : parseFloat(e.target.value) }))}
                          style={{ width:110, marginTop:4, background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:"5px 8px", fontSize:12, fontWeight:600, textAlign:"right" }} />
                      </td>
                      <td style={{ padding:"11px 6px", textAlign:"right", color:C.muted, fontSize:11 }}>{x.key === "doe" ? `${v.doe.toLocaleString("en-AU")} coll.` : "—"}</td>
                      <td style={{ padding:"11px 6px", textAlign:"right", color:C.text, fontWeight:600 }}>{x.bankedEx ? fmt(x.bankedEx) : "—"}</td>
                      <td style={{ padding:"11px 6px", textAlign:"right", color: x.estimated ? C.amber : C.green, fontWeight:700 }}>{fmt(x.projEx)}{x.estimated && x.projEx > 0 ? " *" : ""}</td>
                      <td style={{ padding:"11px 6px", textAlign:"right", color:C.muted }}>{x.prevEx ? fmt(x.prevEx) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop:`2px solid ${C.border}` }}>
                    <td style={{ padding:"12px 6px", color:C.label, fontWeight:700 }}>TOTAL (ex GST)</td>
                    <td/>
                    <td/>
                    <td style={{ padding:"12px 6px", textAlign:"right", color:C.text, fontWeight:800 }}>{fmt(totalMtdEx)}</td>
                    <td style={{ padding:"12px 6px", textAlign:"right", color:C.green, fontWeight:800 }}>{fmt(totalProjEx)}</td>
                    <td/>
                  </tr>
                  <tr>
                    <td style={{ padding:"4px 6px 12px", color:C.blue, fontWeight:700 }}>TOTAL (incl. GST)</td>
                    <td/>
                    <td/>
                    <td style={{ padding:"4px 6px 12px", textAlign:"right", color:C.blue, fontWeight:800 }}>{fmt(totalMtdIncl)}</td>
                    <td style={{ padding:"4px 6px 12px", textAlign:"right", color:C.blue, fontWeight:800 }}>{fmt(totalProjIncl)}</td>
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p style={{ fontSize:11, color:C.muted, margin:"14px 0 0", lineHeight:1.6 }}>
              <span style={{ fontWeight:600, color:C.label }}>Method: </span>
              Toggle <b>Pay period</b> (28-day, matches your RCTI &amp; invoices) or <b>Calendar month</b>.
              <b> Parcels</b> = every 310.94c delivered unit (Ordinary/Express + Signature + eParcel), live from <code>parcel_events</code> (the WAHR feed);
              <b> Depot / Drop</b> = the <code>depots</code> table at the Drop Rate. Both project volume-to-date to {unit.toLowerCase()}-end by operating days ({opDaysElapsed}/{opDaysWin}, Mon–Sat).
              <b> Night Sort</b> = a contract line item AP pays without an invoice — the scheduled Sun–Thu nights at a flat ${NIGHT_PER_NIGHT.toLocaleString("en-AU", { minimumFractionDigits: 2 })} a night, so nights already run are banked.
              <b> DoE</b> and <b>EP (2nd Wave)</b> show the ex-GST total of the tax invoice you generate for that period (exact match once issued).
              Until then they show a <span style={{ color:C.amber }}>projection (amber *)</span>: <b>EP</b> = live Express volume × ${EP_WAVE_NET_RATE.toFixed(2)};
              <b> DoE</b> = your most recent generated DoE invoice, or the standard invoice (${DOE_PERIOD_EX.toFixed(0)}/period) if none yet. Type in a box to override. Unit rates are editable and session-only.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}

// AP RCTI Period_End is DD/MM/YYYY → ISO (matches canonical 28-day period ends).
const AP_END_TO_ISO = (s) => {
  const m = (s || "").toString().trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[3]}-${String(+m[2]).padStart(2, "0")}-${String(+m[1]).padStart(2, "0")}`;
  return (s || "").toString().slice(0, 10);
};

// Engine total (incl. GST) for a completed/whole pay period — same pricing as the Cash
// Flow tab: parcels+depot volume × AP rate, plus DoE/EP/Night from the stored invoice for
// the period (exact), else the pricing model. `vol` = {parcels,express,doe,depot} for the period.
function engineTotalForPeriod(vol, pStart, pEnd, invoices) {
  const v = vol || { parcels: 0, express: 0, doe: 0, depot: 0 };
  const inv = (type) => invoices.find((iv) => iv.type === type && iv.periodEnd === pEnd);
  const nc = countNightShifts(pStart, pEnd, pEnd);
  const parcelsEx = (v.parcels + v.express) * DEFAULT_RATES.parcels;
  const depotEx = v.depot * DEFAULT_RATES.depot;
  const waveEx  = inv("wave")  ? Number(inv("wave").subtotalEx) || 0  : v.express * EP_WAVE_NET_RATE;
  const nightEx = (nc.sun + nc.wk) * NIGHT_PER_NIGHT;   // contract line item — never invoiced
  const extraEx = inv("extra") ? Number(inv("extra").subtotalEx) || 0 : 0;   // only when actually raised
  const lastDoe = invoices.filter((iv) => iv.type === "doe").sort((a, b) => (b.periodEnd || "").localeCompare(a.periodEnd || ""))[0];
  const doeEx   = inv("doe")   ? Number(inv("doe").subtotalEx) || 0   : (lastDoe ? Number(lastDoe.subtotalEx) || 0 : DOE_PERIOD_EX);
  return (parcelsEx + depotEx + waveEx + nightEx + extraEx + doeEx) * GST;
}

// AP RCTI coverage & reconciliation — every pay period as a row: your uploaded AP RCTI
// (Actual Paid) vs the engine total, with MISSING periods flagged for upload.
function ApRctiReconView({ apRevenue, onUpload }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [count, setCount]   = useState(13);
  const [colMode, setColMode] = useState("both");   // 'both' | 'actual' | 'engine'
  const [series, setSeries]   = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const periods = useMemo(() => recentPeriods(count), [count]);   // newest first
  const rangeStart = periods[periods.length - 1].start;
  const rangeEnd = periods[0].end;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const [ps, ti] = await Promise.all([
          fetch("/.netlify/functions/revenue-forecast-sb", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "getPeriodSeries", start: rangeStart, end: rangeEnd }) }).then((r) => r.json()),
          fetch("/.netlify/functions/tax-invoices-sb", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "getTaxInvoices" }) }).then((r) => r.json()).catch(() => ({ success: false })),
        ]);
        if (!alive) return;
        if (!ps.success) throw new Error(ps.error || "Period series failed");
        setSeries(ps.periods || {}); setInvoices(ti.success ? (ti.invoices || []) : []);
      } catch (e) { if (alive) setErr(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [count]);

  const actualByEnd = useMemo(() => {
    const m = {};
    (apRevenue || []).forEach((r) => { const iso = AP_END_TO_ISO(r.Period_End); if (iso) m[iso] = r; });
    return m;
  }, [apRevenue]);

  const rows = periods.map((p) => {
    const actual = actualByEnd[p.end] || null;
    const actualIncl = actual ? parseMoney(actual.Total_Inc_GST) : null;
    const engineIncl = series ? engineTotalForPeriod(series[p.end], p.start, p.end, invoices) : null;
    const variance = (actualIncl != null && engineIncl != null) ? engineIncl - actualIncl : null;
    const complete = p.end < todayISO;
    const isCurrent = p.start <= todayISO && p.end >= todayISO;
    return { ...p, actual, actualIncl, engineIncl, variance, complete, isCurrent };
  });

  const completeRows = rows.filter((r) => r.complete);
  const uploaded = completeRows.filter((r) => r.actual).length;
  const missing = completeRows.filter((r) => !r.actual);

  const showActual = colMode !== "engine";
  const showEngine = colMode !== "actual";
  const showVar = colMode === "both";

  return (
    <div>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:700, margin:"0 0 4px" }}>AP RCTIs — Coverage &amp; Reconciliation</h2>
          <p style={{ fontSize:13, color:C.muted, margin:0 }}>Actual paid RCTIs received from AP vs engine totals, by pay period. Missing periods are flagged to upload.</p>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ display:"flex", background:C.surface, borderRadius:10, padding:3, border:`1px solid ${C.border}` }}>
            {[["both","Both"],["actual","Actual"],["engine","Engine"]].map(([id,lbl]) => (
              <button key={id} onClick={() => setColMode(id)} style={{ padding:"7px 14px", borderRadius:8, border:"none", background:colMode===id?C.blue:"transparent", color:colMode===id?"#fff":C.muted, fontWeight:700, fontSize:12, cursor:"pointer" }}>{lbl}</button>
            ))}
          </div>
          <select value={count} onChange={(e) => setCount(+e.target.value)} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, padding:"8px 12px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
            {[13,26,39].map((n) => <option key={n} value={n}>Last {n} periods</option>)}
          </select>
        </div>
      </div>

      {/* Coverage summary */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(180px, 100%),1fr))", gap:14, marginBottom:16 }}>
        <KpiCard label="AP RCTIs Uploaded" value={`${uploaded} / ${completeRows.length}`} icon={CheckCircle} accent={C.green} sub="of completed periods" />
        <KpiCard label="Missing RCTIs" value={String(missing.length)} icon={AlertCircle} accent={missing.length?C.red:C.green} sub={missing.length?"click a row to upload":"all periods covered"} />
        <KpiCard label="Actual Paid (uploaded)" value={fmtK(rows.reduce((s,r)=>s+(r.actualIncl||0),0))} icon={DollarSign} accent={C.blue} sub="incl. GST · sum of uploaded" />
        <KpiCard label="Engine Total (shown)" value={fmtK(rows.reduce((s,r)=>s+(r.engineIncl||0),0))} icon={TrendingUp} accent={C.purple} sub="incl. GST · all periods" />
      </div>

      {missing.length > 0 && (
        <div style={{ marginBottom:16, padding:"12px 16px", borderRadius:10, background:`${C.red}12`, border:`1px solid ${C.red}33`, color:C.red, fontSize:13 }}>
          <b>{missing.length} completed period{missing.length>1?"s":""} missing an AP RCTI:</b> {missing.map((m)=>m.label).join(" · ")}. Upload each from AP so revenue reconciles.
        </div>
      )}

      <Card>
        {loading ? (
          <div style={{ textAlign:"center", padding:50, color:C.muted, fontSize:13 }}>Loading engine totals…</div>
        ) : err ? (
          <div style={{ color:C.red, fontSize:13, padding:12 }}>Couldn’t load: {err}</div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:640 }}>
              <thead><tr style={{ borderBottom:`1px solid ${C.border}` }}>
                {["Pay period", ...(showActual?["Actual Paid (AP)"]:[]), ...(showEngine?["Engine total"]:[]), ...(showVar?["Variance"]:[]), "Status"].map((h,i) => (
                  <th key={h} style={{ textAlign:i===0?"left":"right", padding:"9px 8px", color:C.label, fontWeight:600, fontSize:11, textTransform:"uppercase", letterSpacing:"0.05em" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const varPct = (r.variance != null && r.actualIncl) ? r.variance / r.actualIncl : null;
                  const vc = varPct == null ? C.muted : Math.abs(varPct) <= 0.02 ? C.green : Math.abs(varPct) <= 0.1 ? C.amber : C.red;
                  return (
                    <tr key={r.key} style={{ borderBottom:`1px solid ${C.border}22`, background: r.isCurrent ? `${C.blue}0c` : "transparent" }}>
                      <td style={{ padding:"11px 8px" }}>
                        <div style={{ color:C.text, fontWeight:600 }}>{r.label}</div>
                        <div style={{ color:C.muted, fontSize:11 }}>ends {r.end}{r.isCurrent?" · current":""}</div>
                      </td>
                      {showActual && (
                        <td style={{ padding:"11px 8px", textAlign:"right" }}>
                          {r.actual
                            ? <div><div style={{ color:C.text, fontWeight:700 }}>{fmt(r.actualIncl)}</div><div style={{ color:C.muted, fontSize:10 }}>SAP {r.actual.SAP_Invoice_No||"—"}</div></div>
                            : (r.complete
                                ? <button onClick={onUpload} style={{ background:`${C.red}18`, border:`1px solid ${C.red}55`, color:C.red, borderRadius:7, padding:"5px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>MISSING — Upload ⤒</button>
                                : <span style={{ color:C.muted, fontSize:12 }}>not issued yet</span>)}
                        </td>
                      )}
                      {showEngine && <td style={{ padding:"11px 8px", textAlign:"right", color: r.isCurrent?C.blue:C.text, fontWeight:600 }}>{r.engineIncl!=null?fmt(r.engineIncl):"—"}{r.isCurrent?" *":""}</td>}
                      {showVar && <td style={{ padding:"11px 8px", textAlign:"right", color:vc, fontWeight:600 }}>{r.variance!=null?`${r.variance>0?"+":""}${fmt(r.variance)}`:"—"}{varPct!=null?<span style={{ color:C.muted, fontSize:10 }}> {(varPct*100).toFixed(1)}%</span>:null}</td>}
                      <td style={{ padding:"11px 8px", textAlign:"right" }}>
                        {r.actual ? <span style={{ color:C.green, fontSize:12, fontWeight:700 }}>✓ uploaded</span>
                          : r.complete ? <span style={{ color:C.red, fontSize:12, fontWeight:700 }}>✗ missing</span>
                          : <span style={{ color:C.muted, fontSize:12 }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p style={{ fontSize:11, color:C.muted, margin:"14px 0 0", lineHeight:1.6 }}>
          <b>Actual Paid</b> = the AP RCTI you imported (incl. GST), matched to its pay period. <b>Engine total</b> = the same
          computation as the Cash Flow tab (live volume × AP rates + your DoE/EP/Night Sort invoices/models). Current period (*)
          is a projection. A <span style={{ color:C.red }}>MISSING</span> row is a completed period with no imported RCTI — click it to upload.
        </p>
      </Card>
    </div>
  );
}

export default function RevenuePage() {
  const { rctis } = useApp();
  const [activeView,     setActiveView]     = useState("cashflow");
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [apRevenue,      setApRevenue]      = useState([]);
  const [apPva,          setApPva]          = useState([]);
  const [payroll,        setPayroll]        = useState([]);
  const [expenses,       setExpenses]       = useState([]);
  const [rctiRows,       setRctiRows]       = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [showUpload,     setShowUpload]     = useState(false);
  const [openKpi,        setOpenKpi]        = useState(null);   // period view: 'revenue' | 'cost' | null
  const [openMonthly,    setOpenMonthly]    = useState(null);   // monthly view: 'revenue' | 'cost' | null
  const [openAnnual,     setOpenAnnual]     = useState(null);   // annual view: FY key (calendar year) | null
  const [lastExtracted,  setLastExtracted]  = useState(null);

  // Derive live RCTI rows from context (populated after "Build RCTIs from Sheets")
  const liveRctiRows = useMemo(() => rctis.map(r => ({
    Invoice_No:   r.invoiceNumber,
    Login:        r.driverName,
    Company:      r.companyName || r.personalName || '',
    Parcels:      String((r.lineItems || []).find(li => li.description?.includes('Parcel'))?.quantity || 0),
    Gross_Total:  String(r.total        || 0),   // actual payment incl. GST (matches ABA)
    Lease_Fee:    String(r.deductions?.leaseFee || 0),
    Net_Payable:  String(r.subtotal     || 0),
    Period_Label: r.periodLabel || '',
  })), [rctis]);

  // Merge: a period is only "covered by live" if it has auto-built (non-manual) RCTIs.
  // Manual-only periods fall back to Sheets RCTI_History so historical data isn't blocked.
  const effectiveRctiRows = useMemo(() => {
    const liveBuiltPeriods = new Set(
      rctis.filter(r => !r.isManual).map(r => r.periodLabel).filter(Boolean)
    );
    return [
      // All live rows for periods that have auto-built data
      ...liveRctiRows.filter(r => liveBuiltPeriods.has(r.Period_Label)),
      // Sheets fallback for every other period (includes periods with only manual RCTIs)
      ...rctiRows.filter(r => !liveBuiltPeriods.has(r.Period_Label)),
    ];
  }, [liveRctiRows, rctiRows, rctis]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res  = await fetch("/.netlify/functions/sheets");
      if (!res.ok) throw new Error("Sheets fetch failed");
      const data = await res.json();
      setApRevenue(data.apRevenue   || []);
      setApPva    (data.apPva       || []);
      setPayroll  (data.payroll     || []);
      setExpenses (data.expenses    || []);
      setRctiRows (data.rctiHistory || []);
      const periods = (data.apRevenue||[]).map(r=>r.Period_Label).filter(Boolean);
      if (periods.length) setSelectedPeriod(periods[periods.length-1]);
    } catch(e) { setError(e.message); }
    finally    { setLoading(false); }
  };

  useEffect(()=>{ loadData(); },[]);

  const handleUploadSuccess = (extracted) => { setLastExtracted(extracted); setTimeout(()=>loadData(), 1500); };

  const allPeriods = useMemo(()=>{
    const s=new Set([...apRevenue.map(r=>r.Period_Label),...payroll.map(r=>r.Period_Label),...effectiveRctiRows.map(r=>r.Period_Label)]);
    return [...s].filter(Boolean).sort();
  },[apRevenue,payroll,effectiveRctiRows]);

  const periodData = useMemo(()=>allPeriods.map(label=>{
    const rev  = apRevenue.find(r=>r.Period_Label===label);
    const pvas = apPva.filter(r=>r.Period_Label===label);
    const pay  = payroll.find(r=>r.Period_Label===label);
    const exps = expenses.filter(r=>r.Period_Label===label);
    const baseRevenue        = parseMoney(rev?.Total_Ex_GST);
    const pvaTotal           = pvas.reduce((s,p)=>s+parseMoney(p.Amount_Approved),0);
    const totalRevenue       = baseRevenue;
    const totalRevenueInclGST = Math.round(totalRevenue * 1.1 * 100) / 100;
    const periodRctiRows = effectiveRctiRows.filter(r => r.Period_Label === label);
    // contractorGross = sum of actual bank payments (incl. GST, after lease deduction)
    const contractorGross = periodRctiRows.reduce((s, r) => s + parseMoney(r.Gross_Total), 0);
    // leaseFeeTotal = HP Leasing recovery — netted from contractor cost below
    const leaseFeeTotal   = periodRctiRows.filter(r => parseMoney(r.Gross_Total) > 0).reduce((s, r) => s + parseMoney(r.Lease_Fee), 0);
    // contractorCost = net of HP Leasing recovery (matches ABA bank run)
    const contractorCost  = contractorGross - leaseFeeTotal;
    const payrollCost  = parseMoney(pay?.Total_Cost);
    const expenseCost  = exps.reduce((s,e)=>s+parseMoney(e.Amount_Ex_GST),0);
    const totalCost    = contractorCost+payrollCost+expenseCost;
    // All on GST-inclusive basis: Revenue (incl. GST) − Contractor Payments (incl. GST) − Payroll
    const grossMargin  = totalRevenueInclGST-totalCost;
    const marginPct    = totalRevenueInclGST>0?grossMargin/totalRevenueInclGST:null;
    const contributionMargin    = grossMargin;
    const contributionMarginPct = marginPct;
    const {start,end}  = parsePeriodLabel(label);
    return {
      label,start,end,totalRevenue,totalRevenueInclGST,baseRevenue,pvaTotal,
      contractorCost,contractorGross,leaseFeeTotal,payrollCost,expenseCost,
      totalCost,grossMargin,marginPct,
      contributionMargin,contributionMarginPct,
      nightSortHours:parseMoney(pay?.Night_Sort_Hours),
      xeroRef:pay?.Xero_Ref||"—", sapInvoice:rev?.SAP_Invoice_No||"—",
      pvas,rev,pay, contractorRows: periodRctiRows,
      ordinary:parseMoney(rev?.Ordinary_Express), signature:parseMoney(rev?.Signature_Items),
      eParcel:parseMoney(rev?.eParcel_Consignments), eParcelAdd:parseMoney(rev?.eParcel_Additional),
      dropRate:parseMoney(rev?.Drop_Rate), det:parseMoney(rev?.DET_Service), shuttle:parseMoney(rev?.Shuttle_Rate),
    };
  }),[allPeriods,apRevenue,apPva,payroll,expenses,effectiveRctiRows]);

  const selected   = useMemo(()=>periodData.find(p=>p.label===selectedPeriod)||null,[periodData,selectedPeriod]);
  const prevPeriod = useMemo(()=>{const i=periodData.findIndex(p=>p.label===selectedPeriod);return i>0?periodData[i-1]:null;},[periodData,selectedPeriod]);
  const trend = (c,p)=>p&&p!==0?((c-p)/p)*100:0;

  const monthlyData = useMemo(()=>{
    const m={};
    periodData.forEach(p=>{ if(!p.end)return; const key=`${p.end.getFullYear()}-${String(p.end.getMonth()+1).padStart(2,"0")}`; const label=p.end.toLocaleString("en-AU",{month:"short",year:"numeric"}); if(!m[key])m[key]={label,revenue:0,cost:0}; m[key].revenue+=p.totalRevenueInclGST; m[key].cost+=p.totalCost; });
    return Object.entries(m).sort(([a],[b])=>a.localeCompare(b)).map(([,v])=>v);
  },[periodData]);

  const annualData = useMemo(()=>{
    const a={};
    periodData.forEach(p=>{ if(!p.end)return; const yr=String(p.end.getFullYear()); if(!a[yr])a[yr]={label:`FY${yr}`,revenue:0,cost:0}; a[yr].revenue+=p.totalRevenueInclGST; a[yr].cost+=p.totalCost; });
    return Object.values(a);
  },[periodData]);

  if (loading) return (<div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:320,background:C.bg }}><div style={{ textAlign:"center" }}><div style={{ width:40,height:40,border:`3px solid ${C.blue}33`,borderTop:`3px solid ${C.blue}`,borderRadius:"50%",animation:"spin 1s linear infinite",margin:"0 auto 16px" }}/><p style={{ color:C.muted,fontSize:14 }}>Loading revenue data…</p></div></div>);
  if (error) return <div style={{ padding:40,textAlign:"center",color:C.red,fontSize:14 }}>Error: {error}</div>;

  const hasData = periodData.length > 0;

  return (
    <div style={{ minHeight:"100vh",background:C.bg,padding:"32px 24px",fontFamily:"'DM Sans','Segoe UI',sans-serif",color:C.text }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg);}} @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); select option{background:#0d1829;}`}</style>

      {/* Header */}
      <div style={{ marginBottom:24,display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:16 }}>
        <div>
          <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:4 }}>
            <div style={{ width:38,height:38,borderRadius:10,background:`linear-gradient(135deg,${C.blue},#5b2cf6)`,display:"flex",alignItems:"center",justifyContent:"center" }}><TrendingUp size={19} color="white"/></div>
            <h1 style={{ fontSize:24,fontWeight:800,margin:0,letterSpacing:"-0.02em" }}>Revenue &amp; Margin</h1>
          </div>
          <p style={{ fontSize:13,color:C.muted,margin:0 }}>Hollypaul Recruitment · Australia Post Contract</p>
        </div>
        <div style={{ display:"flex",gap:10,alignItems:"center",flexWrap:"wrap" }}>
          <button onClick={()=>setShowUpload(v=>!v)} style={{ display:"flex",alignItems:"center",gap:7,padding:"9px 16px",borderRadius:9,border:`1px solid ${C.blue}55`,background:showUpload?`${C.blue}22`:"transparent",color:C.blue,fontWeight:700,fontSize:13,cursor:"pointer",transition:"all 0.15s" }}><Upload size={14}/> Import AP RCTI</button>
          <div style={{ display:"flex",background:C.surface,borderRadius:12,padding:4,gap:2,flexWrap:"wrap",border:`1px solid ${C.border}` }}>
            {VIEWS.map(({id,label,Icon})=>(<button key={id} onClick={()=>setActiveView(id)} style={{ display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:8,border:"none",background:activeView===id?`linear-gradient(135deg,${C.blue},#5b2cf6)`:"transparent",color:activeView===id?"white":C.muted,fontWeight:activeView===id?700:500,fontSize:13,cursor:"pointer",transition:"all 0.15s" }}><Icon size={13}/>{label}</button>))}
          </div>
        </div>
      </div>

      {showUpload && (<div style={{ marginBottom:24,display:"grid",gridTemplateColumns:lastExtracted?"1fr 1fr":"1fr",gap:16 }}><UploadPanel onSuccess={handleUploadSuccess}/>{lastExtracted && <ExtractedPreview data={lastExtracted}/>}</div>)}

      {/* Cash Flow + AP RCTIs — render regardless of imported-actuals state */}
      {activeView==="cashflow" && <CashFlowView/>}
      {activeView==="reconcile" && <ApRctiReconView apRevenue={apRevenue} onUpload={()=>{ setShowUpload(true); try{ window.scrollTo({ top:0, behavior:"smooth" }); }catch{} }}/>}

      {activeView!=="cashflow" && activeView!=="reconcile" && !hasData && !showUpload && (<div style={{ marginBottom:24 }}><EmptyBanner msg="No revenue data yet. Click 'Import AP RCTI' above to upload your first invoice PDF."/></div>)}

      {activeView!=="cashflow" && activeView!=="reconcile" && hasData && (
        <>
          {activeView==="period" && (
            <div>
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:12 }}>
                <div><h2 style={{ fontSize:18,fontWeight:700,margin:"0 0 4px" }}>Pay Period Summary</h2><p style={{ fontSize:13,color:C.muted,margin:0 }}>28-day billing cycle</p></div>
                <select value={selectedPeriod} onChange={e=>setSelectedPeriod(e.target.value)} style={{ background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,padding:"8px 14px",fontSize:13,fontWeight:600,cursor:"pointer" }}>
                  {allPeriods.map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              {selected ? (
                <>
                  {/* KPI Cards */}
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(200px, 100%),1fr))",gap:14,marginBottom:24 }}>
                    <KpiCard label="Total Revenue (incl. GST)" value={fmtK(selected.totalRevenueInclGST)} trendPct={trend(selected.totalRevenueInclGST,prevPeriod?.totalRevenueInclGST)} sub={`Ex GST: ${fmtK(selected.totalRevenue)} · vs prior period`} icon={TrendingUp} accent={C.blue}
                      onClick={()=>setOpenKpi(o=>o==="revenue"?null:"revenue")} open={openKpi==="revenue"}/>
                    <KpiCard label="Contractor Payments (net of lease)" value={fmtK(selected.contractorCost)} icon={Users} accent={C.purple} sub={`${selected.contractorRows?.length||0} contractors · Gross: ${fmtK(selected.contractorGross)}`}/>
                    <KpiCard label="Payroll Cost" value={fmtK(selected.payrollCost)} sub={[selected.nightSortHours>0?`${selected.nightSortHours} hrs night sort`:null,selected.xeroRef&&selected.xeroRef!=="—"?`Ref: ${selected.xeroRef}`:null].filter(Boolean).join(' · ')||undefined} icon={Clock} accent={C.amber}/>
                    <KpiCard label="Gross Margin $" value={fmtK(selected.grossMargin)} trendPct={trend(selected.grossMargin,prevPeriod?.grossMargin)} sub="vs prior period" icon={DollarSign} accent={selected.marginPct>=0.1?C.green:C.red}/>
                    <KpiCard label="Margin %" value={pct(selected.marginPct)} trendPct={trend(selected.marginPct||0,prevPeriod?.marginPct||0)} sub="vs prior period" icon={BarChart2} accent={selected.marginPct>=0.1?C.green:C.amber}/>
                    <KpiCard label="Van Lease Recovery" value={fmtK(selected.leaseFeeTotal)} icon={TrendingUp} accent={C.teal} sub="HP Leasing — netted from contractor payments"/>
                    <KpiCard label="Total Cost" value={fmtK(selected.totalCost)} trendPct={trend(selected.totalCost,prevPeriod?.totalCost)} sub="vs prior period" icon={TrendingDown} accent={C.red}
                      onClick={()=>setOpenKpi(o=>o==="cost"?null:"cost")} open={openKpi==="cost"}/>
                  </div>

                  {openKpi==="revenue" && (
                    <BreakdownPanel title={`Revenue by AP line item — ${selected.label} (incl. GST)`}
                      rows={[
                        ["Ordinary / Express Parcels", selected.ordinary,   C.blue],
                        ["Signature Items",            selected.signature,  C.teal],
                        ["eParcel Consignments",       selected.eParcel,    C.purple],
                        ["eParcel Additional",         selected.eParcelAdd, C.lime],
                        ["Drop Rate",                  selected.dropRate,   C.amber],
                        ["DET Service",                selected.det,        C.green],
                        ["Shuttle Rate",               selected.shuttle,    C.red],
                      ].map(([label,exVal,color])=>({ label, color, amount:(exVal||0)*1.1 }))}/>
                  )}
                  {openKpi==="cost" && (
                    <BreakdownPanel title={`Cost by category — ${selected.label}`} totalLabel="TOTAL COST"
                      rows={[
                        { label:"Contractor Payments (net of lease)", color:C.purple, amount:selected.contractorCost },
                        { label:"Employee Payroll",                   color:C.amber,  amount:selected.payrollCost },
                        { label:"Other Expenses",                     color:C.muted,  amount:selected.expenseCost },
                      ]}/>
                  )}

                  {/* Margin banner */}
                  <div style={{ marginBottom:16,padding:"14px 20px",borderRadius:10,background:`${C.lime}0e`,border:`1px solid ${C.lime}33`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12 }}>
                    <div>
                      <span style={{ fontSize:12,fontWeight:700,color:C.lime,textTransform:"uppercase",letterSpacing:"0.07em" }}>Gross Margin</span>
                      <span style={{ fontSize:12,color:C.muted,marginLeft:10 }}>Revenue (incl. GST) − Contractor Payments (net of lease) − Payroll</span>
                    </div>
                    <div style={{ fontSize:13,color:C.muted,whiteSpace:"nowrap" }}>
                      {fmt(selected.totalRevenueInclGST)} <span style={{ color:C.border }}>−</span> {fmt(selected.contractorCost)} <span style={{ color:C.border }}>−</span> {fmt(selected.payrollCost)} <span style={{ color:C.border }}>=</span>{" "}
                      <span style={{ fontSize:17,fontWeight:800,color:C.lime }}>{fmt(selected.grossMargin)}</span>
                      <span style={{ fontSize:12,color:C.lime,marginLeft:6 }}>({pct(selected.marginPct)})</span>
                    </div>
                  </div>

                  <div className="stack-mobile" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16 }}>
                    <Card>
                      <SectionTitle>AP Revenue Breakdown</SectionTitle>
                      <div style={{ fontSize:12,color:C.muted,marginBottom:12 }}>SAP {selected.sapInvoice} · {selected.label}</div>
                      {[
                        ["Ordinary / Express Parcels",selected.ordinary,false],
                        ["Signature Items",selected.signature,false],
                        ["eParcel Consignments",selected.eParcel,false],
                        ["eParcel Additional",selected.eParcelAdd,false],
                        ["Drop Rate",selected.dropRate,false],
                        ["DET Service",selected.det,false],
                        ["Shuttle Rate",selected.shuttle,false],
                        ["PVAs Total",selected.pvaTotal,true],
                      ].map(([lbl,val,bold])=>(
                        <div key={lbl} style={{ display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}22` }}>
                          <span style={{ fontSize:13,color:bold?C.text:C.label,fontWeight:bold?700:400 }}>{lbl}</span>
                          <span style={{ fontSize:13,fontWeight:600,color:bold?C.green:C.text }}>{fmt(val)}</span>
                        </div>
                      ))}
                      <div style={{ display:"flex",justifyContent:"space-between",padding:"8px 0 0",marginTop:4 }}>
                        <span style={{ fontSize:13,color:C.label }}>Total (ex GST)</span>
                        <span style={{ fontSize:13,fontWeight:600,color:C.text }}>{fmt(selected.totalRevenue)}</span>
                      </div>
                      <div style={{ display:"flex",justifyContent:"space-between",padding:"8px 0 0" }}>
                        <span style={{ fontSize:14,fontWeight:700,color:C.text }}>Total (incl. GST)</span>
                        <span style={{ fontSize:14,fontWeight:800,color:C.blue }}>{fmt(selected.totalRevenueInclGST)}</span>
                      </div>
                    </Card>
                    <Card>
                      <SectionTitle>Cost Breakdown</SectionTitle>
                      {[
                        ["Contractor Payments (net of lease)", selected.contractorCost,  C.purple],
                        ["Employee Payroll",                   selected.payrollCost,     C.amber ],
                        ["Other Expenses",                     selected.expenseCost,     C.muted ],
                      ].map(([lbl,val,col])=>(
                        <div key={lbl} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${C.border}22` }}>
                          <div style={{ display:"flex",alignItems:"center",gap:8 }}><div style={{ width:10,height:10,borderRadius:2,background:col }}/><span style={{ fontSize:13,color:C.label }}>{lbl}</span></div>
                          <span style={{ fontSize:13,fontWeight:600,color:C.text }}>{fmt(val)}</span>
                        </div>
                      ))}
                      {/* Van Lease Recovery — already netted from contractor payments above */}
                      {selected.leaseFeeTotal > 0 && (
                        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}22` }}>
                          <div style={{ display:"flex",alignItems:"center",gap:8 }}><div style={{ width:10,height:10,borderRadius:2,background:C.teal }}/><span style={{ fontSize:12,color:C.teal,fontStyle:"italic" }}>Van Lease Recovery (HP Leasing) — netted above</span></div>
                          <span style={{ fontSize:12,fontWeight:600,color:C.teal,fontStyle:"italic" }}>−{fmt(selected.leaseFeeTotal)}</span>
                        </div>
                      )}
                      <div style={{ display:"flex",justifyContent:"space-between",padding:"12px 0 0",marginTop:4 }}>
                        <span style={{ fontSize:14,fontWeight:700,color:C.text }}>Total Cost</span>
                        <span style={{ fontSize:14,fontWeight:800,color:C.red }}>{fmt(selected.totalCost)}</span>
                      </div>
                      {selected.pay && <div style={{ marginTop:12,padding:"10px 14px",background:C.surface,borderRadius:8,fontSize:12,color:C.muted }}>Xero Ref: {selected.xeroRef} · Night Sort: {selected.nightSortHours||"—"} hrs</div>}

                      {/* Gross Margin box */}
                      <div style={{ marginTop:16,padding:16,borderRadius:10,background:selected.marginPct>=0.1?`${C.green}15`:selected.marginPct>=0?`${C.amber}15`:`${C.red}15`,border:`1px solid ${selected.marginPct>=0.1?C.green:selected.marginPct>=0?C.amber:C.red}44` }}>
                        <div style={{ fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em" }}>Gross Margin</div>
                        <div style={{ fontSize:20,fontWeight:800,color:selected.marginPct>=0.1?C.green:selected.marginPct>=0?C.amber:C.red }}>
                          {fmt(selected.grossMargin)} <span style={{ fontSize:13 }}>({pct(selected.marginPct)})</span>
                        </div>
                        <div style={{ fontSize:11,color:C.muted,marginTop:4 }}>Revenue incl. GST · Contractor payments net of lease</div>
                      </div>
                    </Card>
                  </div>

                  <Card style={{ marginBottom:16 }}>
                    <SectionTitle>Payment Variation Authorisations — DoE · Night Sort · 2nd Wave Express</SectionTitle>
                    <PvaTable pvas={selected.pvas}/>
                  </Card>
                  <Card>
                    <SectionTitle>Contractor RCTI Reconciliation — {selected.contractorRows?.length||0} invoices</SectionTitle>
                    <ContractorTable rows={selected.contractorRows||[]}/>
                  </Card>
                </>
              ) : <EmptyBanner msg="Select a period above to view details."/>}
            </div>
          )}

          {activeView==="daily"   && <div><h2 style={{ fontSize:18,fontWeight:700,margin:"0 0 8px" }}>Daily View</h2><EmptyBanner msg="Daily per-driver profit/loss — coming in next build iteration."/></div>}
          {activeView==="weekly"  && <div><h2 style={{ fontSize:18,fontWeight:700,margin:"0 0 8px" }}>Weekly View</h2><EmptyBanner msg="Weekly rollup available once multiple pay periods of data are entered."/></div>}

          {activeView==="monthly" && (
            <div>
              <h2 style={{ fontSize:18,fontWeight:700,margin:"0 0 24px" }}>Monthly Rollup</h2>
              {monthlyData.length===0?<EmptyBanner msg="No monthly data yet."/>:(
                <><div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(200px, 100%),1fr))",gap:14,marginBottom:24 }}>
                  <KpiCard label="Total Revenue (all months)" value={fmtK(monthlyData.reduce((s,m)=>s+m.revenue,0))} icon={TrendingUp} accent={C.blue}
                    onClick={()=>setOpenMonthly(o=>o==="revenue"?null:"revenue")} open={openMonthly==="revenue"}/>
                  <KpiCard label="Total Cost (all months)" value={fmtK(monthlyData.reduce((s,m)=>s+m.cost,0))} icon={TrendingDown} accent={C.red}
                    onClick={()=>setOpenMonthly(o=>o==="cost"?null:"cost")} open={openMonthly==="cost"}/>
                </div>
                {openMonthly==="revenue" && <BreakdownPanel title="Revenue by AP line item — all months (incl. GST)" rows={revenueRows(periodData)}/>}
                {openMonthly==="cost" && <BreakdownPanel title="Cost by category — all months" totalLabel="TOTAL COST" rows={costRows(periodData)}/>}
                <Card><SectionTitle>Revenue vs Cost by Month</SectionTitle><HistoChart data={monthlyData}/></Card>
                <Card style={{ marginTop:16 }}><SectionTitle>Month Detail</SectionTitle>{monthlyData.map((m,i)=><BarRow key={i} label={m.label} revenue={m.revenue} cost={m.cost} max={Math.max(...monthlyData.map(d=>d.revenue))}/>)}</Card></>
              )}
            </div>
          )}

          {activeView==="annual" && (
            <div>
              <h2 style={{ fontSize:18,fontWeight:700,margin:"0 0 24px" }}>Annual Summary</h2>
              {annualData.length===0?<EmptyBanner msg="No annual data yet."/>:(
                <><div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(200px, 100%),1fr))",gap:14,marginBottom:24 }}>
                  {annualData.map((a,i)=>{ const yr=a.label.replace("FY",""); return (
                    <KpiCard key={i} label={a.label+" Revenue"} value={fmtK(a.revenue)} icon={TrendingUp} accent={C.blue}
                      onClick={()=>setOpenAnnual(o=>o===yr?null:yr)} open={openAnnual===yr}/>
                  ); })}
                </div>
                {openAnnual && (() => {
                  const yrPeriods = periodData.filter(p => p.end && String(p.end.getFullYear()) === openAnnual);
                  return (<>
                    <BreakdownPanel title={`Revenue by AP line item — FY${openAnnual} (incl. GST)`} rows={revenueRows(yrPeriods)}/>
                    <BreakdownPanel title={`Cost by category — FY${openAnnual}`} totalLabel="TOTAL COST" rows={costRows(yrPeriods)}/>
                  </>);
                })()}
                <Card><SectionTitle>Revenue vs Cost by Year</SectionTitle><HistoChart data={annualData} height={180}/></Card></>
              )}
            </div>
          )}

          {activeView==="historical" && (
            <div>
              <h2 style={{ fontSize:18,fontWeight:700,margin:"0 0 24px" }}>Historical — All Periods</h2>
              <Card style={{ marginBottom:24 }}>
                <SectionTitle>Revenue vs Cost — All Pay Periods</SectionTitle>
                <HistoChart data={periodData.map(p=>({label:p.end?p.end.toLocaleString("en-AU",{day:"numeric",month:"short"}):p.label,revenue:p.totalRevenue,cost:p.totalCost}))} height={160}/>
              </Card>
              <Card style={{ marginBottom:16 }}>
                <SectionTitle>Period-by-Period Detail</SectionTitle>
                {periodData.map((p,i)=><BarRow key={i} label={p.label} revenue={p.totalRevenue} cost={p.totalCost} max={Math.max(...periodData.map(d=>d.totalRevenue))} sub={p.marginPct!==null?`Margin ${pct(p.marginPct)}`:undefined}/>)}
              </Card>
              <Card style={{ borderColor:`${C.blue}55` }}>
                <SectionTitle>📋 Tender Data Accumulator — AP Contract Jun 2027</SectionTitle>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(170px, 100%),1fr))",gap:14 }}>
                  {[
                    ["Periods on Record",          periodData.length+" periods"],
                    ["Total Revenue",               fmtK(periodData.reduce((s,p)=>s+p.totalRevenue,0))],
                    ["Total Cost",                  fmtK(periodData.reduce((s,p)=>s+p.totalCost,0))],
                    ["Avg Gross Margin %",           pct(periodData.filter(p=>p.marginPct!==null).reduce((s,p)=>s+(p.marginPct||0),0)/(periodData.filter(p=>p.marginPct!==null).length||1))],
                    ["Avg Contribution Margin %",   pct(periodData.filter(p=>p.contributionMarginPct!==null).reduce((s,p)=>s+(p.contributionMarginPct||0),0)/(periodData.filter(p=>p.contributionMarginPct!==null).length||1))],
                    ["Total PVA Approved",          fmtK(periodData.reduce((s,p)=>s+p.pvaTotal,0))],
                    ["Total Lease Fees",            fmtK(periodData.reduce((s,p)=>s+p.leaseFeeTotal,0))],
                    ["Total Payroll Cost",          fmtK(periodData.reduce((s,p)=>s+p.payrollCost,0))],
                  ].map(([lbl,val])=>(
                    <div key={lbl} style={{ background:C.surface,borderRadius:10,padding:"14px 16px",border:`1px solid ${C.border}` }}>
                      <div style={{ fontSize:11,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em" }}>{lbl}</div>
                      <div style={{ fontSize:18,fontWeight:800,color:C.text }}>{val}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
